import type { HomeInsight } from "components/Signals/Dashboard/HomeInsights.models";
import type { SlimLLMClassifierResponse } from "models/LLMs";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import { PatientJourneyStage } from "stores/ApplicationFilters/Filters/PatientJourneyStage";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import BaseService from "./BaseService";

type ProactiveInsightResponse = {
    id: string;
    insightType: string;
    summary: string;
    delta: number;
    currentMatchingConversationCount?: number | null;
    appliedFilters?: ProactiveInsightAppliedFilters | string | null;
};

export type ProactiveInsightAppliedFilters = {
    currentStart?: string;
    currentEnd?: string;
    baselineStart?: string;
    baselineEnd?: string;
    hierarchyId?: string;
    hierarchyName?: string;
    DriverIndex?: string;
    driverName?: string;
    // Retained for proactive insights generated before the driver-model change.
    TopicNumber?: string;
    topicName?: string;
    JourneyStage?: string;
    journeyStageName?: string;
};

export type ProactiveInsightViewInConversationsPayload = {
    filters: ApplicationFilters;
    smartBannerFilters: Partial<ApplicationFilters>;
    hierarchyName?: string;
};

export type ProactiveInsightSubscriptionPayload = {
    filters: ApplicationFilters;
    kboAnalysisPrompt: string;
};

export type ProactiveInsightKboFilterContext = {
    contextType: "ProactiveInsight";
    filters: ApplicationFilters;
    insightTitle: string;
    contextSummary: string;
};

export type AnalyzeProactiveInsightRequest = {
    insightType: string;
    appliedFilterContext: ProactiveInsightKboFilterContext;
    additionalInformation: {
        analysisSubject: string;
        frontendPersisted: true;
    };
};

export type ProactiveInsightDefinition = {
    id: string;
    insightType: string;
    threshold: number;
    isActive: boolean;
    organizationStructureMemberIds: string[];
    previousRunInsightCount?: number | null;
};

export type CreateProactiveInsightDefinitionRequest = {
    insightType: string;
    threshold: number;
    isActive: boolean;
    organizationStructureMemberIds: string[];
};

export type UpdateProactiveInsightDefinitionRequest = {
    threshold: number;
    isActive: boolean;
    organizationStructureMemberIds: string[];
};

export type ProactiveInsightSettings = {
    isEnabled: boolean;
    runTime?: string | null;
};

export type UpdateProactiveInsightSettingsRequest = {
    isEnabled: boolean;
    runTime?: string | null;
    organizationStructureMemberIds: string[];
};

export type ProactiveInsightRunStatus =
    | "Awaiting"
    | "Ranking"
    | "Complete"
    | "Failed";

export type ProactiveInsightRunDetails = {
    periodStart: string;
    periodEnd: string;
    hasRun: boolean;
    runId?: string | null;
    status?: ProactiveInsightRunStatus | null;
    expectedTypeCount?: number | null;
    checkedInTypeCount?: number | null;
    expectedRunCount?: number | null;
    completedRunCount?: number | null;
    createdOn?: string | null;
    modifiedOn?: string | null;
    canRun: boolean;
    canRerun: boolean;
    isStaleInProgress: boolean;
};

export const proactiveInsightTypes = {
    EddyRateByTopic: "EddyRateByTopic",
    EddyRateByJourneyStage: "EddyRateByJourneyStage",
    RepeatCallsByTopic: "RepeatCallsByTopic",
    DriversOfTreatmentDisruption: "DriversOfTreatmentDisruption",
    DriversOfUnresolvedCalls: "DriversOfUnresolvedCalls",
    UnresolvedRepeatCalls: "UnresolvedRepeatCalls",
    BeforeTreatmentEddiesByTopic: "BeforeTreatmentEddiesByTopic",
    TreatmentDisruptionRate: "TreatmentDisruptionRate",
    RepeatCallRate: "RepeatCallRate",
    CallResolutionRate: "CallResolutionRate",
} as const;

export const proactiveInsightIconTypes: {
    eddy: ReadonlySet<string>;
    risk: ReadonlySet<string>;
    recurring: ReadonlySet<string>;
    resolution: ReadonlySet<string>;
} = {
    eddy: new Set<string>([proactiveInsightTypes.EddyRateByTopic]),
    risk: new Set<string>([
        proactiveInsightTypes.EddyRateByJourneyStage,
        proactiveInsightTypes.DriversOfTreatmentDisruption,
        proactiveInsightTypes.BeforeTreatmentEddiesByTopic,
        proactiveInsightTypes.TreatmentDisruptionRate,
    ]),
    recurring: new Set<string>([
        proactiveInsightTypes.RepeatCallsByTopic,
        proactiveInsightTypes.RepeatCallRate,
    ]),
    resolution: new Set<string>([
        proactiveInsightTypes.DriversOfUnresolvedCalls,
        proactiveInsightTypes.UnresolvedRepeatCalls,
        proactiveInsightTypes.CallResolutionRate,
    ]),
};

export const normalizeProactiveInsightType = (insightType: string) =>
    insightType.replace(/\s+/g, "");

const proactiveInsightTypesNeedingLlmClassifiers = new Set<string>([
    proactiveInsightTypes.RepeatCallsByTopic,
    proactiveInsightTypes.DriversOfUnresolvedCalls,
    proactiveInsightTypes.UnresolvedRepeatCalls,
    proactiveInsightTypes.RepeatCallRate,
    proactiveInsightTypes.CallResolutionRate,
]);

export const proactiveInsightNeedsLlmClassifiers = (insightType: string) =>
    proactiveInsightTypesNeedingLlmClassifiers.has(insightType);

export const insightTypeToTitle: Record<string, string> = {
    [proactiveInsightTypes.EddyRateByTopic]: "Eddy Rate by Driver",
    [proactiveInsightTypes.EddyRateByJourneyStage]:
        "Eddy Rate by Journey Stage",
    [proactiveInsightTypes.RepeatCallsByTopic]: "Repeat Calls by Driver",
    [proactiveInsightTypes.DriversOfTreatmentDisruption]:
        "Drivers of Treatment Disruption",
    [proactiveInsightTypes.DriversOfUnresolvedCalls]:
        "Drivers of Unresolved Calls",
    [proactiveInsightTypes.UnresolvedRepeatCalls]: "Unresolved Repeat Calls",
    [proactiveInsightTypes.BeforeTreatmentEddiesByTopic]:
        "Before Treatment Eddies by Driver",
    [proactiveInsightTypes.TreatmentDisruptionRate]:
        "Treatment Disruption Rate",
    [proactiveInsightTypes.RepeatCallRate]: "Repeat Call Rate",
    [proactiveInsightTypes.CallResolutionRate]: "Call Resolution Rate",
};

const insightTypeToLabel: Record<
    string,
    {
        increase: string;
        decrease: string;
    }
> = {
    [proactiveInsightTypes.EddyRateByTopic]: {
        increase: "Experience Friction",
        decrease: "Experience Improvement",
    },
    [proactiveInsightTypes.EddyRateByJourneyStage]: {
        increase: "Journey Risk",
        decrease: "Journey Improvement",
    },
    [proactiveInsightTypes.RepeatCallsByTopic]: {
        increase: "Recurring Issue",
        decrease: "Issue Improvement",
    },
    [proactiveInsightTypes.DriversOfTreatmentDisruption]: {
        increase: "Journey Risk",
        decrease: "Journey Improvement",
    },
    [proactiveInsightTypes.DriversOfUnresolvedCalls]: {
        increase: "Resolution Risk",
        decrease: "Resolution Improvement",
    },
    [proactiveInsightTypes.UnresolvedRepeatCalls]: {
        increase: "Resolution Risk",
        decrease: "Resolution Improvement",
    },
    [proactiveInsightTypes.BeforeTreatmentEddiesByTopic]: {
        increase: "Journey Risk",
        decrease: "Journey Improvement",
    },
    [proactiveInsightTypes.TreatmentDisruptionRate]: {
        increase: "Journey Risk",
        decrease: "Journey Improvement",
    },
    [proactiveInsightTypes.RepeatCallRate]: {
        increase: "Recurring Issue",
        decrease: "Issue Improvement",
    },
    [proactiveInsightTypes.CallResolutionRate]: {
        increase: "Resolution Improvement",
        decrease: "Resolution Risk",
    },
};

function getInsightLabel(insightType: string, delta: number) {
    const mapping = insightTypeToLabel[insightType];
    if (!mapping) {
        return "Proactive Insight";
    }

    return delta < 0 ? mapping.decrease : mapping.increase;
}

function parseAppliedFilters(
    appliedFilters?: ProactiveInsightAppliedFilters | string | null,
): ProactiveInsightAppliedFilters | null {
    if (!appliedFilters) {
        return null;
    }

    if (typeof appliedFilters === "string") {
        try {
            return JSON.parse(appliedFilters) as ProactiveInsightAppliedFilters;
        } catch {
            return null;
        }
    }

    return appliedFilters;
}

const journeyStageMap: Record<string, PatientJourneyStage> = {
    "before treatment": PatientJourneyStage.BeforeTreatment,
    "on treatment": PatientJourneyStage.OnTreatment,
    "after treatment": PatientJourneyStage.AfterTreatment,
    "restarting treatment": PatientJourneyStage.RestartingTreatment,
    "treatment disruption": PatientJourneyStage.TreatmentDisruption,
    "no clear journey stage": PatientJourneyStage.NoClearJourneyStage,
};

function resolveJourneyStage(stage?: string): PatientJourneyStage | undefined {
    if (!stage) {
        return undefined;
    }

    return journeyStageMap[stage.trim().toLowerCase()];
}

function resolveModelResultFilter(
    classifierLabel: string,
    result: string,
    llmClassifiers?: SlimLLMClassifierResponse[],
): SlimLLMClassifierResponse | null {
    const matchingClassifier = llmClassifiers?.find((classifier) =>
        classifier.name.toLowerCase().includes(classifierLabel.toLowerCase()),
    );

    if (!matchingClassifier) {
        return null;
    }

    const matchingResult =
        matchingClassifier.results.find(
            (item) => item.toLowerCase() === result.toLowerCase(),
        ) ?? result;

    return {
        id: matchingClassifier.id,
        name: matchingClassifier.name,
        results: [matchingResult],
    };
}

function getBaseConversationFilters(
    appliedFilters: ProactiveInsightAppliedFilters,
): ApplicationFilters {
    return {
        startDate: appliedFilters.currentStart,
        endDate: appliedFilters.currentEnd,
        dateReferenceOption: DateReferenceOption.InteractionDate,
        hierarchyIds: appliedFilters.hierarchyId
            ? [appliedFilters.hierarchyId]
            : [],
        evaluationTypes: [],
        mediaTypes: [],
        callDirections: [],
        containsTopics: true,
        containsPrimaryTopics: true,
    };
}

export function buildProactiveInsightViewInConversationsPayload(
    insightType: string,
    appliedFilters: ProactiveInsightAppliedFilters,
    llmClassifiers?: SlimLLMClassifierResponse[],
): ProactiveInsightViewInConversationsPayload | null {
    const filters = getBaseConversationFilters(appliedFilters);
    const smartBannerFilters: Partial<ApplicationFilters> = {
        startDate: filters.startDate,
        endDate: filters.endDate,
    };

    const topicNumber = appliedFilters.TopicNumber
        ? Number(appliedFilters.TopicNumber)
        : undefined;
    const topicName = appliedFilters.topicName;
    const driverIndex = appliedFilters.DriverIndex
        ? Number(appliedFilters.DriverIndex)
        : undefined;
    const driverName = appliedFilters.driverName;
    const journeyStageName =
        appliedFilters.journeyStageName ?? appliedFilters.JourneyStage;
    const journeyStage = resolveJourneyStage(journeyStageName);

    if (
        driverIndex !== undefined &&
        Number.isInteger(driverIndex) &&
        driverName
    ) {
        filters.drivers = [{ driverIndex, driverName }];
        filters.containsDrivers = true;
        smartBannerFilters.drivers = filters.drivers;
        smartBannerFilters.containsDrivers = true;
    } else if (topicNumber && topicName) {
        filters.primaryTopics = [{ topicNumber, topicLabel: topicName }];
        smartBannerFilters.primaryTopics = filters.primaryTopics;
        smartBannerFilters.containsPrimaryTopics = true;
    }

    if (journeyStage !== undefined) {
        filters.patientJourneyStages = [journeyStage];
        smartBannerFilters.patientJourneyStages = filters.patientJourneyStages;
    }

    switch (insightType) {
        case proactiveInsightTypes.EddyRateByTopic:
        case proactiveInsightTypes.EddyRateByJourneyStage:
            filters.eddyEffectStatus = true;
            smartBannerFilters.eddyEffectStatus = true;
            break;
        case proactiveInsightTypes.BeforeTreatmentEddiesByTopic:
            filters.eddyEffectStatus = true;
            filters.patientJourneyStages = [
                PatientJourneyStage.BeforeTreatment,
            ];
            smartBannerFilters.eddyEffectStatus = true;
            smartBannerFilters.patientJourneyStages =
                filters.patientJourneyStages;
            break;
        case proactiveInsightTypes.DriversOfTreatmentDisruption:
            filters.patientJourneyStages = [
                PatientJourneyStage.TreatmentDisruption,
            ];
            smartBannerFilters.patientJourneyStages =
                filters.patientJourneyStages;
            break;
        case proactiveInsightTypes.TreatmentDisruptionRate:
            filters.patientJourneyStages = [
                PatientJourneyStage.TreatmentDisruption,
            ];
            smartBannerFilters.patientJourneyStages =
                filters.patientJourneyStages;
            break;
        case proactiveInsightTypes.RepeatCallsByTopic: {
            const repeatCaller = resolveModelResultFilter(
                "Repeat Caller",
                "Yes",
                llmClassifiers,
            );
            if (!repeatCaller) return null;
            filters.modelResults = [repeatCaller];
            smartBannerFilters.modelResults = filters.modelResults;
            break;
        }
        case proactiveInsightTypes.DriversOfUnresolvedCalls: {
            const callResolution = resolveModelResultFilter(
                "Call Resolution",
                "No",
                llmClassifiers,
            );
            if (!callResolution) return null;
            filters.modelResults = [callResolution];
            smartBannerFilters.modelResults = filters.modelResults;
            break;
        }
        case proactiveInsightTypes.UnresolvedRepeatCalls: {
            const repeatCaller = resolveModelResultFilter(
                "Repeat Caller",
                "Yes",
                llmClassifiers,
            );
            const callResolution = resolveModelResultFilter(
                "Call Resolution",
                "No",
                llmClassifiers,
            );
            if (!repeatCaller || !callResolution) return null;
            filters.modelResults = [callResolution, repeatCaller];
            smartBannerFilters.modelResults = filters.modelResults;
            break;
        }
        case proactiveInsightTypes.RepeatCallRate: {
            const repeatCaller = resolveModelResultFilter(
                "Repeat Caller",
                "Yes",
                llmClassifiers,
            );
            if (!repeatCaller) return null;
            filters.modelResults = [repeatCaller];
            smartBannerFilters.modelResults = filters.modelResults;
            break;
        }
        case proactiveInsightTypes.CallResolutionRate: {
            const callResolution = resolveModelResultFilter(
                "Call Resolution",
                "Yes",
                llmClassifiers,
            );
            if (!callResolution) return null;
            filters.modelResults = [callResolution];
            smartBannerFilters.modelResults = filters.modelResults;
            break;
        }
    }

    return {
        filters,
        smartBannerFilters,
        hierarchyName: appliedFilters.hierarchyName,
    };
}

type KboPromptContext = {
    driverName?: string;
    topicName?: string;
    journeyStageName?: string;
};

function primaryModelSubject(ctx: KboPromptContext): string {
    if (ctx.driverName) {
        return `the primary driver is ${ctx.driverName}`;
    }

    if (ctx.topicName) {
        return `the primary topic is ${ctx.topicName}`;
    }

    return "the selected driver is present";
}

export const proactiveInsightKboPrompts: Record<
    string,
    (ctx: KboPromptContext) => string
> = {
    [proactiveInsightTypes.EddyRateByTopic]: (ctx) =>
        `Analyze the recent conversations in this hierarchy where Eddy is present and ${primaryModelSubject(
            ctx,
        )}. Summarize what is driving the increase in customer friction, what customers are struggling with most, and any notable patterns or themes emerging in these conversations.`,
    [proactiveInsightTypes.EddyRateByJourneyStage]: ({ journeyStageName }) =>
        `Analyze the recent conversations in this hierarchy where Eddy is present during the ${
            journeyStageName ?? "selected"
        } stage. Explain what is driving friction at this point in the patient journey, what customers are experiencing, and what trends or patterns are emerging.`,
    [proactiveInsightTypes.RepeatCallsByTopic]: (ctx) =>
        `Analyze the recent repeat caller conversations in this hierarchy where ${primaryModelSubject(
            ctx,
        )}. Summarize why customers may be needing to call back about this issue, what patterns are showing up across these conversations, and what appears to be driving repeat contact.`,
    [proactiveInsightTypes.DriversOfTreatmentDisruption]: (ctx) =>
        `Analyze the recent conversations in this hierarchy where the patient journey stage is Treatment Disruption and ${primaryModelSubject(
            ctx,
        )}. Summarize what appears to be driving treatment disruption, the key barriers customers are facing, and any notable trends in these conversations.`,
    [proactiveInsightTypes.DriversOfUnresolvedCalls]: (ctx) =>
        `Analyze the recent unresolved conversations in this hierarchy where ${primaryModelSubject(
            ctx,
        )}. Summarize why these issues may not be getting resolved, what common patterns are showing up, and what appears to be contributing most to unresolved customer needs.`,
    [proactiveInsightTypes.UnresolvedRepeatCalls]: () =>
        `Analyze the recent conversations in this hierarchy that were both unresolved and from repeat callers. Summarize what is causing customers to call back without getting resolution, the main issues or barriers showing up, and any trends that may explain this pattern.`,
    [proactiveInsightTypes.BeforeTreatmentEddiesByTopic]: (ctx) =>
        `Analyze the recent conversations in this hierarchy where Eddy is present before treatment and ${primaryModelSubject(
            ctx,
        )}. Summarize what is driving customer friction before treatment begins, what obstacles customers are encountering, and any emerging patterns in these conversations.`,
    [proactiveInsightTypes.TreatmentDisruptionRate]: () =>
        `Analyze the recent conversations in this hierarchy where the patient journey stage is Treatment Disruption. Summarize what appears to be driving these disruptions, the most common barriers or issues customers are experiencing, and any meaningful trends emerging over time.`,
    [proactiveInsightTypes.RepeatCallRate]: () =>
        `Analyze the recent repeat caller conversations in this hierarchy. Summarize the main reasons customers are needing to contact again, the most common patterns across these conversations, and any emerging trends that may be driving repeat contact.`,
    [proactiveInsightTypes.CallResolutionRate]: () =>
        `Analyze the recent resolved conversations in this hierarchy. Summarize what appears to be contributing to successful resolution, the common patterns across these conversations, and any notable trends that may explain why these issues are being resolved effectively.`,
};

export function buildProactiveInsightKboPrompt(
    insightType: string,
    appliedFilters: ProactiveInsightAppliedFilters,
): string | null {
    const builder = proactiveInsightKboPrompts[insightType];
    if (!builder) return null;

    return builder({
        driverName: appliedFilters.driverName,
        topicName: appliedFilters.topicName,
        journeyStageName:
            appliedFilters.journeyStageName ?? appliedFilters.JourneyStage,
    });
}

export function buildProactiveInsightKboFilterContext(
    insightType: string,
    appliedFilters: ProactiveInsightAppliedFilters,
    insightTitle: string,
    llmClassifiers?: SlimLLMClassifierResponse[],
): ProactiveInsightKboFilterContext | null {
    const payload = buildProactiveInsightViewInConversationsPayload(
        insightType,
        appliedFilters,
        llmClassifiers,
    );

    if (!payload) return null;

    const contextSummary = [
        `Insight: ${insightTitle}`,
        appliedFilters.hierarchyName
            ? `Hierarchy: ${appliedFilters.hierarchyName}`
            : null,
        appliedFilters.driverName
            ? `Driver: ${appliedFilters.driverName}`
            : appliedFilters.topicName
              ? `Topic: ${appliedFilters.topicName}`
              : null,
        appliedFilters.journeyStageName
            ? `Journey stage: ${appliedFilters.journeyStageName}`
            : null,
    ]
        .filter(Boolean)
        .join("\n");

    return {
        contextType: "ProactiveInsight",
        filters: payload.filters,
        insightTitle,
        contextSummary,
    };
}

export function buildProactiveInsightSubscriptionPayload(
    insightType: string,
    appliedFilters: ProactiveInsightAppliedFilters,
    llmClassifiers?: SlimLLMClassifierResponse[],
): ProactiveInsightSubscriptionPayload | null {
    const viewPayload = buildProactiveInsightViewInConversationsPayload(
        insightType,
        appliedFilters,
        llmClassifiers,
    );

    if (!viewPayload) {
        return null;
    }

    return {
        filters: viewPayload.filters,
        kboAnalysisPrompt:
            buildProactiveInsightKboPrompt(insightType, appliedFilters) ?? "",
    };
}

export class ProactiveInsightsService extends BaseService {
    protected urlBase = "api/ProactiveInsights";

    public async listDefinitions() {
        const params = new URLSearchParams();
        return await this.get<ProactiveInsightDefinition[]>(
            params,
            "ListDefinitions",
        );
    }

    public async createDefinition(
        request: CreateProactiveInsightDefinitionRequest,
    ) {
        const params = new URLSearchParams();
        return await this.post<ProactiveInsightDefinition>(
            JSON.stringify(request),
            params,
            "CreateDefinition",
            { returnJson: true },
        );
    }

    public async updateDefinition(
        id: string,
        request: UpdateProactiveInsightDefinitionRequest,
    ) {
        const params = new URLSearchParams();
        return await this.put<ProactiveInsightDefinition>(
            params,
            `UpdateDefinition/${id}`,
            JSON.stringify(request),
            { returnJson: true },
        );
    }

    public async getSettings() {
        return await this.get<ProactiveInsightSettings>(
            new URLSearchParams(),
            "Settings",
        );
    }

    public async updateSettings(
        request: UpdateProactiveInsightSettingsRequest,
    ) {
        return await this.put<ProactiveInsightSettings>(
            new URLSearchParams(),
            "Settings",
            JSON.stringify(request),
            { returnJson: true },
        );
    }

    public async getRunDetails() {
        return await this.get<ProactiveInsightRunDetails>(
            new URLSearchParams(),
            "RunDetails",
        );
    }

    public async run(rerun: boolean) {
        return await this.post<ProactiveInsightRunDetails>(
            JSON.stringify({ rerun }),
            new URLSearchParams(),
            "Run",
        );
    }

    public async getLatest(hierarchyIds?: string[]) {
        const params = new URLSearchParams();
        (hierarchyIds ?? []).forEach((id) => {
            if (id) {
                params.append("hierarchyIds", id);
            }
        });
        const response = await this.get<ProactiveInsightResponse[]>(
            params,
            "GetLatest",
        );

        return response.map(
            (insight): HomeInsight => ({
                id: insight.id,
                insightType: insight.insightType,
                delta: insight.delta,
                label: getInsightLabel(insight.insightType, insight.delta),
                summary: insight.summary,
                currentMatchingConversationCount:
                    insight.currentMatchingConversationCount,
                appliedFilters: parseAppliedFilters(insight.appliedFilters),
            }),
        );
    }
}
